import React, { useMemo } from 'react'
import { Mail } from 'lucide-react'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { sanitizeHtml } from '@/utils/sanitizeHtml'
import type {
  EmailMessageReceivedEventData,
  EmailMessageSentEventData,
} from '@super/shared-types'

interface EmailDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  emailData: EmailMessageReceivedEventData | EmailMessageSentEventData | null
}

export const EmailDrawer: React.FC<EmailDrawerProps> = ({
  open,
  onOpenChange,
  emailData,
}) => {
  if (!emailData) return null

  const subject = emailData.subject || '(No Subject)'
  const from = emailData.from
  const to = emailData.to
  const cc = emailData.cc
  const timestamp = new Date(emailData.timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  const parsedBody = emailData.parsedBody

  // Sanitize HTML content to prevent XSS attacks
  const htmlContent = useMemo(
    () => (emailData.html ? sanitizeHtml(emailData.html) : null),
    [emailData.html],
  )

  // Use visible text if available, otherwise fall back to messageText
  const textContent = parsedBody?.visibleText || emailData.messageText

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto p-0"
      >
        <div className="flex flex-col h-full bg-white dark:bg-background">
          {/* Header */}
          <div className="px-8 pt-8 pb-5 border-b border-gray-100 dark:border-border">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex-shrink-0">
                <Mail className="h-4 w-4 text-gray-400 dark:text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground leading-snug mb-3">
                  {subject}
                </h2>

                {/* Email Metadata */}
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 dark:text-muted-foreground font-medium min-w-[40px] flex-shrink-0">
                      From
                    </span>
                    <span className="text-gray-700 dark:text-foreground flex-1 break-all">
                      {from}
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 dark:text-muted-foreground font-medium min-w-[40px] flex-shrink-0">
                      To
                    </span>
                    <span className="text-gray-700 dark:text-foreground flex-1 break-all">
                      {to}
                    </span>
                  </div>
                  {cc && (
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400 dark:text-muted-foreground font-medium min-w-[40px] flex-shrink-0">
                        Cc
                      </span>
                      <span className="text-gray-700 dark:text-foreground flex-1 break-all">
                        {cc}
                      </span>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 dark:text-muted-foreground font-medium min-w-[40px] flex-shrink-0">
                      Date
                    </span>
                    <span className="text-gray-600 dark:text-muted-foreground">
                      {timestamp}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Email Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            {htmlContent ? (
              <div
                className="email-content text-[13px]
                  prose prose-sm max-w-none dark:prose-invert
                  prose-headings:text-gray-900 dark:prose-headings:text-foreground prose-headings:font-semibold prose-headings:text-sm
                  prose-p:text-gray-700 dark:prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:text-[13px] prose-p:my-3
                  prose-a:text-blue-600 dark:prose-a:text-info prose-a:no-underline hover:prose-a:underline prose-a:text-[13px]
                  prose-strong:text-gray-900 dark:prose-strong:text-foreground prose-strong:font-semibold prose-strong:text-[13px]
                  prose-code:text-gray-800 dark:prose-code:text-foreground prose-code:bg-gray-50 dark:prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-normal
                  prose-pre:bg-gray-50 dark:prose-pre:bg-muted prose-pre:text-gray-800 dark:prose-pre:text-foreground prose-pre:text-xs prose-pre:border prose-pre:border-gray-200 dark:prose-pre:border-border
                  prose-blockquote:border-l-gray-300 dark:prose-blockquote:border-l-border prose-blockquote:text-gray-600 dark:prose-blockquote:text-muted-foreground prose-blockquote:text-[13px] prose-blockquote:pl-4
                  prose-hr:border-gray-200 dark:prose-hr:border-border
                  prose-ul:text-gray-700 dark:prose-ul:text-muted-foreground prose-ul:text-[13px] prose-ul:my-3
                  prose-ol:text-gray-700 dark:prose-ol:text-muted-foreground prose-ol:text-[13px] prose-ol:my-3
                  prose-li:text-gray-700 dark:prose-li:text-muted-foreground prose-li:text-[13px] prose-li:my-1"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            ) : (
              <div className="text-[13px] text-gray-700 dark:text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {textContent}
              </div>
            )}

            {/* Show quoted text if available */}
            {parsedBody?.quotedText && (
              <details className="mt-8 group">
                <summary className="cursor-pointer text-[11px] font-medium text-gray-400 dark:text-muted-foreground hover:text-gray-600 dark:hover:text-foreground transition-colors flex items-center gap-1.5 select-none py-1">
                  <span className="inline-block group-open:rotate-90 transition-transform text-[10px]">
                    ▶
                  </span>
                  Show quoted message
                </summary>
                <div className="mt-3 pl-3 border-l-2 border-gray-100 dark:border-border text-[11px] text-gray-500 dark:text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {parsedBody.quotedText}
                </div>
              </details>
            )}

            {/* Show signature if available */}
            {parsedBody?.signature && (
              <details className="mt-4 group">
                <summary className="cursor-pointer text-[11px] font-medium text-gray-400 dark:text-muted-foreground hover:text-gray-600 dark:hover:text-foreground transition-colors flex items-center gap-1.5 select-none py-1">
                  <span className="inline-block group-open:rotate-90 transition-transform text-[10px]">
                    ▶
                  </span>
                  Show signature
                </summary>
                <div className="mt-3 pl-3 border-l-2 border-gray-100 dark:border-border text-[11px] text-gray-500 dark:text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {parsedBody.signature}
                </div>
              </details>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
